/**
 * Pure validation functions for contact form fields.
 * No React dependencies — fully testable in isolation.
 */

export interface ContactFormData {
  firstname: string
  lastname: string
  email: string
  company: string
  phone?: string
  note?: string
}

export interface FormErrors {
  firstname?: string
  lastname?: string
  email?: string
  company?: string
  phone?: string
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Accepts formats: (123) 456-7890, 123-456-7890, 123.456.7890,
// 1234567890, +1 123 456 7890
const PHONE_REGEX =
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,3}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/

export function validateEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

export function validatePhone(phone: string): boolean {
  return PHONE_REGEX.test(phone.replace(/\s/g, ''))
}

export interface FieldLabels {
  readonly firstNameLabel: string
  readonly lastNameLabel: string
  readonly emailLabel: string
  readonly companyLabel?: string
}

/**
 * Validate all contact form fields, returning an object of field-level errors.
 * Returns an empty object when the form is valid.
 */
export function validateContactForm(
  data: ContactFormData,
  labels: FieldLabels,
): FormErrors {
  const errors: FormErrors = {}

  if (!data.firstname?.trim()) {
    errors.firstname = `${labels.firstNameLabel} is required`
  }
  if (!data.lastname?.trim()) {
    errors.lastname = `${labels.lastNameLabel} is required`
  }
  if (!data.company?.trim()) {
    errors.company = `${labels.companyLabel ?? 'Company Name'} is required`
  }
  if (!data.email?.trim()) {
    errors.email = `${labels.emailLabel} is required`
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address'
  }

  if (data.phone?.trim() && !validatePhone(data.phone)) {
    errors.phone = 'Please enter a valid phone number'
  }

  return errors
}

/**
 * Returns true when the errors object contains no field errors.
 */
export function isFormValid(errors: FormErrors): boolean {
  return Object.keys(errors).length === 0
}

/**
 * Returns true when `value` is a syntactically valid http or https URL.
 * Rejects non-http(s) protocols (javascript:, data:, mailto:, ftp:, etc.)
 * to keep the field safe to render as a link downstream.
 */
export function isValidHttpUrl(value: string): boolean {
  if (!value) return false
  const trimmed = value.trim()
  if (!trimmed) return false
  try {
    const url = new URL(trimmed)
    return url.protocol === "http:" || url.protocol === "https:"
  } catch {
    return false
  }
}
